import React, { useState, useEffect } from 'react';
import { auth, db } from './firebase/config';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { Toaster } from 'react-hot-toast';

// 📂 IMPORTING ALL PAGES
import Layout from './components/Layout';
import OwnerDashboard from './pages/OwnerDashboard';
import CoachDashboard from './pages/CoachDashboard';
import StudentDashboard from './pages/StudentDashboard';
import ChessWorlds from './pages/ChessWorlds';
import Classroom from './pages/Classroom';
import HomeworkManager from './pages/HomeworkManager'; // 🔥 PERFECTLY IMPORTED
import Arena from './pages/Arena';
import Login from './pages/Login';
import Leaderboard from './pages/Leaderboard';
import Tournaments from './pages/Tournaments';
import Analysis from './pages/Analysis';
import Library from './pages/Library';
import Basics from './pages/Basics';
import BoardTools from './pages/BoardTools';

import { getDefaultAvatar } from './utils/avatarData';

// 🔥 All Dummy Components removed successfully!

export default function App() {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');

  const [joinedLiveClass, setJoinedLiveClass] = useState(null);
  const [isClassroomLive, setIsClassroomLive] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (u) {
        setUser(u);
        const userEmail = u.email.toLowerCase();
        const userDocRef = doc(db, 'users', userEmail);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          setRole(userDoc.data().role);
          // ✅ Mark user as online for Play with Friends feature
          updateDoc(userDocRef, { online: true, lastSeen: new Date().toISOString() }).catch(() => {});
        } else {
          let assignedRole = 'STUDENT';
          if (userEmail === 'hayaachessacademy@gmail.com') assignedRole = 'OWNER';
          setRole(assignedRole);
          await setDoc(userDocRef, {
            email: userEmail,
            name: u.displayName || userEmail.split('@')[0],
            role: assignedRole,
            avatar: getDefaultAvatar(userEmail),
            online: true,
            lastSeen: new Date().toISOString(),
            createdAt: new Date().toISOString()
          });
        }
      } else {
        setUser(null); setRole(null);
        // Mark offline on sign-out
      }
      setLoading(false);
    });
    return () => unsub();
  }, []);

  if (loading) return <div style={{height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-primary)', color: '#2563eb' }}>Loading Academy...</div>;
  if (!user) return <Login />;

  return (
    <Layout user={user} role={role} activeTab={activeTab} setActiveTab={setActiveTab} joinedLiveClass={joinedLiveClass} isClassroomLive={isClassroomLive}>

      <Toaster position="top-center" toastOptions={{ style: { background: 'var(--bg-primary)', color: '#fff', border: '1px solid #3b82f6', fontWeight: 'bold' } }} />

      {window.innerWidth <= 900 && (
        <div style={{display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
          <button onClick={() => signOut(auth)} style={{padding: '8px 15px', border: '1px solid #ef4444', background: 'transparent', color: '#ef4444', borderRadius: '6px', cursor: 'pointer' }}>Logout</button>
        </div>
      )}

      <div key={activeTab} className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', flex: 1, height: '100%', minHeight: 0 }}>
        {/* 🚦 ALL NEW PAGE ROUTES */}
        {activeTab === 'classroom' && <Classroom user={user} setIsClassroomLive={setIsClassroomLive} />}
        {activeTab === 'homework' && <HomeworkManager user={user} />} {/* 🔥 NOW IT WILL LOAD REAL FILE */}
        {activeTab === 'basics' && <Basics user={user} />}
        {activeTab === 'chess-worlds' && <ChessWorlds user={user} />}
        {activeTab === 'board-tools' && <BoardTools user={user} setActiveTab={setActiveTab} />}

        {/* 🚦 EXISTING PAGE ROUTES */}
        {activeTab === 'arena' && <Arena user={user} role={role} />}
        {activeTab === 'tournaments' && <Tournaments user={user} role={role} />}
        {activeTab === 'leaderboard' && <Leaderboard />}
        {activeTab === 'analysis' && <Analysis user={user} />}
        {activeTab === 'library' && <Library user={{ ...user, role }} />}

        {/* 🏠 DASHBOARDS */}
        {activeTab === 'dashboard' && (
          <>
            {(role === 'OWNER' || role === 'ADMIN') && <OwnerDashboard user={user} />}
            {(role === 'CHIEF_COACH' || role === 'COACH') && <CoachDashboard user={user} setActiveTab={setActiveTab} />}
            {(role === 'STUDENT' || !role) && <StudentDashboard user={user} joinedLiveClass={joinedLiveClass} setJoinedLiveClass={setJoinedLiveClass} setActiveTab={setActiveTab} />}
          </>
        )}
      </div>
    </Layout>
  );
}
